import React, { useCallback, useEffect, useImperativeHandle, useRef, type Ref } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { Spinner } from '@aibuild/ui';
import Chat, { type ChatDraft } from '@components/Chat';
import { useChat, type ToolExecution, type UseChatOptions } from '@hooks/useChat';
import { useTurnFinished } from '@hooks/useTurnFinished';
import { AssistantStatus } from './ScopedAssistantChat.styled';

export interface ScopedAssistantProgress {
  readonly sending: boolean;
  readonly reconnecting: boolean;
  /** The tools of the latest reply, as they run; empty while the latest message is the user's. */
  readonly steps: readonly ToolExecution[];
}

export interface ScopedAssistantControl {
  readonly stop: () => void;
}

export interface ScopedAssistantFirstMessage {
  readonly text: string;
  readonly files?: readonly File[];
}

const NO_STEPS: readonly ToolExecution[] = [];

export interface ScopedAssistantChatProps {
  /** The bound conversation, from {@link useScopedAssistantConversation}. */
  conversation: UseQueryResult<string, Error>;
  /** Called when a turn ends, to pull whatever the assistant saved. */
  onTurnFinished: () => void;
  /** Reports whether a turn is in flight. */
  onBusyChange?: (busy: boolean) => void;
  /** Runs before a send; a false result aborts it. */
  onBeforeSend?: () => Promise<boolean>;
  /** Called once a send goes ahead, for telemetry. */
  onSent?: () => void;
  unavailableMessage: React.ReactNode;
  /** Shown while the conversation is empty; `send` posts a message as if typed. */
  renderEmptyState?: (send: (message: string) => void) => React.ReactNode;
  placeholder: string;
  dataTestId: string;
  /** Screenshots the screen around the chat for each turn the user sends. */
  snapshot?: UseChatOptions['snapshot'];
  onPreviewCapture?: UseChatOptions['onPreviewCapture'];
  /** Replaces what is in the composer for the user to review and send, or sends it when `send` is set. */
  draft?: ChatDraft;
  /** Sent once through the chat itself: the composer holds a draft back while a reconnect runs. */
  initialSend?: ScopedAssistantFirstMessage;
  /** Once the first message's turn is over, or it never got out. */
  onInitialSendSettled?: () => void;
  onProgress?: (progress: ScopedAssistantProgress) => void;
  controlRef?: Ref<ScopedAssistantControl>;
}

/**
 * The chat of an assistant bound to one resource: no runner, no model picker,
 * and a refresh of the resource whenever a turn ends.
 */
const ScopedAssistantChat: React.FC<ScopedAssistantChatProps> = ({
  conversation,
  onTurnFinished,
  onBusyChange,
  onBeforeSend,
  onSent,
  unavailableMessage,
  renderEmptyState,
  placeholder,
  dataTestId,
  snapshot,
  onPreviewCapture,
  draft,
  initialSend,
  onInitialSendSettled,
  onProgress,
  controlRef,
}) => {
  const conversationId = conversation.data ?? null;
  const chat = useChat(conversationId, { snapshot, onPreviewCapture });

  const { cancelRequest, isReconnecting, isHistorySettled } = chat;
  useImperativeHandle(controlRef, () => ({ stop: cancelRequest }), [cancelRequest]);

  const latest = chat.messages.at(-1);
  const steps = (latest?.role === 'assistant' ? latest.toolExecutions : undefined) ?? NO_STEPS;

  // Deliberately `isSending` alone: a reconnect runs on every mount that has
  // a conversation, and reporting it as busy would read as a bug.
  const { isSending } = chat;
  useEffect(() => {
    if (!onBusyChange) return undefined;
    onBusyChange(isSending);
    return () => onBusyChange(false);
  }, [isSending, onBusyChange]);

  useTurnFinished(conversationId, isSending || isReconnecting, onTurnFinished);

  useEffect(() => {
    onProgress?.({ sending: isSending, reconnecting: isReconnecting, steps });
  }, [isReconnecting, isSending, onProgress, steps]);

  const { sendMessage: send } = chat;
  const sendMessage = useCallback(
    async (message: string, files?: File[]) => {
      if (onBeforeSend && !(await onBeforeSend())) return false;
      onSent?.();
      return send(message, files);
    },
    [send, onBeforeSend, onSent],
  );

  const initialSent = useRef(false);
  useEffect(() => {
    if (!initialSend || initialSent.current || !conversationId || !isHistorySettled) return;
    initialSent.current = true;
    sendMessage(initialSend.text, initialSend.files ? [...initialSend.files] : undefined)
      .catch(() => undefined)
      .finally(() => onInitialSendSettled?.());
  }, [conversationId, initialSend, isHistorySettled, onInitialSendSettled, sendMessage]);

  const { retryAfterInterruption: retry } = chat;
  const retryAfterInterruption = useCallback(
    async (interruptedMessageId: string, prompt?: string) => {
      if (onBeforeSend && !(await onBeforeSend())) return;
      onSent?.();
      await retry(interruptedMessageId, prompt);
    },
    [retry, onBeforeSend, onSent],
  );

  // Rendering the composer before the transcript lands lets a send cancel the
  // fetch carrying it, which loses every earlier message (AIB-8372). A cached
  // page still refetches on mount, so the first load is not the only wait.
  if (conversation.isPending || (!!conversation.data && !chat.isHistorySettled)) {
    return (
      <AssistantStatus data-testid={`${dataTestId}__loading`}>
        <Spinner size="sm" />
      </AssistantStatus>
    );
  }

  if (conversation.isError || !conversation.data) {
    return (
      <AssistantStatus role="alert" data-testid={`${dataTestId}__error`}>
        {unavailableMessage}
      </AssistantStatus>
    );
  }

  return (
    <Chat
      messages={chat.messages}
      isLoading={chat.isSending}
      isReconnecting={chat.isReconnecting}
      error={chat.error}
      sendMessage={sendMessage}
      onCancel={chat.cancelRequest}
      onLoadMore={chat.fetchNextPage}
      hasMoreMessages={chat.hasNextPage}
      isLoadingMore={chat.isFetchingNextPage}
      pendingAskUser={chat.pendingAskUser}
      onSubmitAskUser={chat.submitAskUser}
      onSkipAskUser={chat.skipAskUser}
      onRetryAfterInterruption={retryAfterInterruption}
      onSubmitFeedback={chat.submitFeedback}
      onDeleteFeedback={chat.deleteFeedback}
      setupSteps={chat.setupSteps}
      setupExiting={chat.setupExiting}
      compacting={chat.compacting}
      conversationId={conversation.data}
      hideExecutionControls
      inputPlaceholder={placeholder}
      draft={draft}
      emptyState={renderEmptyState?.((message) => {
        sendMessage(message).catch(() => undefined);
      })}
    />
  );
};

export default ScopedAssistantChat;
